import {
  faEllipsis,
  faExternalLink,
  faFlag,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/index';
import { useHistory } from 'react-router-dom';

import { useZuubRcmOnboardingPayers } from '@/react-query/rcm/useZuubRcmOnboarding';

import type { ZuubPatientListItemV3 } from '../../../@types/zuub/contracts';
import type { Appointment } from '../../../react-query/useAppointments';

interface ActionsColumnProps {
  appointment: Appointment | null;
  zuubPatient?: ZuubPatientListItemV3;
  onManagePayerConnection?: (payerId: number) => void;
}

export const ActionsColumn = ({
  zuubPatient,
  onManagePayerConnection,
}: ActionsColumnProps) => {
  const history = useHistory();
  const { data: payers } = useZuubRcmOnboardingPayers();

  const patientId = zuubPatient?.pmsPatientId;

  // Check if we have any valid data to show the button
  const hasValidData = !!zuubPatient && zuubPatient.plans.length > 0;
  const firstPlan = zuubPatient?.plans?.[0];

  // Check conditions that should disable the button
  const isPayerNameMissing = !firstPlan?.payerName;
  const insuranceStatus = firstPlan?.insuranceStatusV2;
  const isInactive = insuranceStatus === 'INACTIVE';
  const isDisabled =
    !patientId || !hasValidData || isPayerNameMissing || isInactive;

  // Get the payer ID - prefer one that needs connection, otherwise use the first one
  const payerId =
    zuubPatient?.plans?.find((data) => data.needsPayerPortalConnection)
      ?.payerId ?? firstPlan?.payerId;

  // Get the portal URL from the payers list
  const payer = payers?.find((p) => p.id === payerId);
  const portalUrl = payer?.portalUrl;

  const handleManagePayerConnection = () => {
    if (payerId && onManagePayerConnection) {
      onManagePayerConnection(payerId);
    }
  };

  const handleGoToPayerPortal = () => {
    if (portalUrl) {
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Hide entire actions column when View Benefits is disabled
  if (isDisabled) {
    return null;
  }

  return (
    <div className="vai-flex vai-gap-2 vai-items-center">
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          history.push(`/auto-verify/patient/${patientId}/benefits`);
        }}
      >
        <Trans>View Benefits</Trans>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="vai-h-8 vai-w-8 vai-p-0">
            <FontAwesomeIcon icon={faEllipsis} className="vai-h-4 vai-w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={handleManagePayerConnection}
            disabled={!payerId || !onManagePayerConnection}
            className="vai-flex vai-items-center vai-justify-between vai-pr-2"
          >
            <span>
              <Trans>Manage Payer Connection</Trans>
            </span>
            <FontAwesomeIcon
              icon={faFlag}
              className="vai-text-sapphire-600 vai-ml-2 !vai-w-3 !vai-h-3 hover:vai-bg-gray-100"
            />
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleGoToPayerPortal}
            disabled={!portalUrl}
            className="vai-flex vai-items-center vai-justify-between vai-pr-2"
          >
            <span>
              <Trans>Go to Payer Portal</Trans>
            </span>
            <FontAwesomeIcon
              icon={faExternalLink}
              className="vai-text-muted-foreground vai-ml-2 !vai-w-3 !vai-h-3"
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
